import type { AdminPickerDetailDto } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { PickerCompany } from './PickerCompany';
import { PiiText } from './PiiText';
import { formatDate, formatDateTime } from '../../../utils/format';
import { placeText } from '../../../utils/place';
import { InfoGrid, InfoItem, SectionTitle } from '../../inspectors/_components/InfoBlocks';

/**
 * «Профиль» block of the drawer (AUT-400) + the «отключён» callout.
 *
 * Links: an active picker shows the live count; a DISABLED one shows
 * «ссылки закрыты» — right after a disable the count can still be non-zero
 * (the revoke sweep is async), but the public page already 404s.
 */
const DisabledCallout = ({
  disabled,
}: {
  disabled: NonNullable<AdminPickerDetailDto['disabled']>;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Alert severity="error">
      <AlertTitle>
        {t('pickers.detail.disabledTitle', { at: formatDateTime(disabled.at) })}
      </AlertTitle>
      {disabled.reason
        ? t('pickers.detail.disabledReason', { reason: disabled.reason })
        : t('pickers.detail.disabledNoReason')}
    </Alert>
  );
};

const LinksValue = ({ picker }: { picker: AdminPickerDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const revoked = t('pickers.detail.linksRevoked', { count: picker.revokedLinkCount });
  if (picker.disabled !== null) {
    return <>{`${t('pickers.linksClosed')} · ${revoked}`}</>;
  }
  const active = t('pickers.detail.linksActive', { count: picker.activeLinkCount });
  return <>{picker.revokedLinkCount > 0 ? `${active} · ${revoked}` : active}</>;
};

export const PickerProfileSection = ({
  picker,
}: {
  picker: AdminPickerDetailDto;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {picker.disabled !== null ? <DisabledCallout disabled={picker.disabled} /> : null}
      <Box>
        <SectionTitle>{t('pickers.detail.profile')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('pickers.columns.phone')}>
            <PiiText value={picker.phone} mono />
          </InfoItem>
          <InfoItem label={t('pickers.columns.city')}>
            {placeText(picker) ?? t('pickers.noValue')}
          </InfoItem>
          <InfoItem label={t('pickers.columns.source')}>
            {t(`pickers.sourceLong.${picker.source}`)}
          </InfoItem>
          <InfoItem label={t('pickers.columns.company')}>
            <PickerCompany company={picker.company} testId="picker-detail-company" />
          </InfoItem>
          <InfoItem label={t('pickers.detail.pickerSince')}>
            {formatDate(picker.createdAt)}
          </InfoItem>
          <InfoItem label={t('pickers.detail.lastActivity')}>
            {formatDate(picker.lastActivityAt)}
          </InfoItem>
          <InfoItem label={t('pickers.detail.links')}>
            <LinksValue picker={picker} />
          </InfoItem>
        </InfoGrid>
      </Box>
    </Box>
  );
};
